import React from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { stripHtml } from '../../../lib/html-text';

export const getBookId = (book: any): string => book.id || book._id;

interface SortableBookListProps {
  books: any[];
  /** Kéo thả bị khoá khi đang lưu thứ tự trước đó */
  disabled?: boolean;
  removingBookId: string | null;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onRemove: (bookId: string) => void;
}

interface SortableBookRowProps {
  book: any;
  position: number;
  disabled: boolean;
  removing: boolean;
  onRemove: (bookId: string) => void;
}

const SortableBookRow: React.FC<SortableBookRowProps> = ({ book, position, disabled, removing, onRemove }) => {
  const bookId = getBookId(book);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: bookId,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-4 border rounded-lg bg-white transition-colors ${
        isDragging ? 'relative z-10 border-blue-500 shadow-lg opacity-90' : 'border-gray-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          disabled={disabled}
          className={`flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 touch-none ${
            disabled ? 'cursor-not-allowed opacity-50' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          aria-label={`Kéo để đổi vị trí sách ${book.title || ''}`.trim()}
          title="Kéo để đổi vị trí"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="7" cy="4" r="1.5" />
            <circle cx="13" cy="4" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="16" r="1.5" />
            <circle cx="13" cy="16" r="1.5" />
          </svg>
        </button>
        <span className="flex-shrink-0 w-6 text-sm font-medium text-gray-500 text-right tabular-nums">{position}</span>
        <div className="flex-shrink-0 w-12 h-16 bg-blue-100 rounded flex items-center justify-center">
          <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <Link
            to={`/books/${bookId}`}
            className="text-base font-medium text-gray-900 hover:text-blue-600 block truncate"
          >
            {book.title || book.name || 'Không có tiêu đề'}
          </Link>
          {stripHtml(book.description) && (
            <p className="text-sm text-gray-500 line-clamp-1">{stripHtml(book.description)}</p>
          )}
        </div>
        {book.status && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            book.status === 'published' || book.status === 'Đã xuất bản'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {book.status === 'published' ? 'Đã xuất bản' : book.status === 'draft' ? 'Nháp' : book.status}
          </span>
        )}
      </div>
      <button
        onClick={() => onRemove(bookId)}
        disabled={removing}
        className="ml-4 text-red-600 hover:text-red-800 disabled:opacity-50"
        title="Xóa sách khỏi bộ sách"
      >
        {removing ? (
          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        )}
      </button>
    </div>
  );
};

/** Danh sách sách trong bộ, kéo tay nắm (hoặc Space + phím mũi tên) để đổi vị trí */
const SortableBookList: React.FC<SortableBookListProps> = ({ books, disabled = false, removingBookId, onReorder, onRemove }) => {
  // Kéo 8px mới bắt đầu, để click vào tên sách/nút xoá không bị nhận nhầm là kéo
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = books.map(getBookId);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(oldIndex, newIndex);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {books.map((book, index) => (
            <SortableBookRow
              key={getBookId(book)}
              book={book}
              position={index + 1}
              disabled={disabled}
              removing={removingBookId === getBookId(book)}
              onRemove={onRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableBookList;
